import Vue from 'vue';
import router from '@/router';
import store from '../';
import { MutationTree } from 'vuex';
import { DateTime } from 'luxon';
import { RestaurantState, CartState, AuthState } from '../types';
import { formatAndSortRotationalMenus } from '@/utils/http';
import { compareMemberItem, clearItemAdjustedPrices, getEligibleSubsidies, compareSubsidyItem, applySubsidiesToRotationalMenus } from '@/utils/pricing';
import { applyCurrencyToI18n } from '@/utils/currency';

export const mutations: MutationTree<RestaurantState> = {

	/**
	 * Set restaurant
	 *
	 * @param {RestaurantState} state
	 * @param {Restaurant} restaurant
	 * @return {void}
	 */
	SET_RESTAURANT(state: RestaurantState, restaurant: Restaurant): void {
		Vue.set(state, 'restaurant', restaurant);
		sessionStorage.setItem('restaurantSlug', restaurant.slug);
		applyCurrencyToI18n(store.getters['restaurant/getCurrency']);
	},

	/**
	 * Set group locations
	 *
	 * @param {RestaurantState} state
	 * @param {GroupLocation[]} groupLocations
	 * @return {void}
	 */
	SET_GROUP_LOCATIONS(state: RestaurantState, groupLocations: GroupLocation[]): void {
		Vue.set(state, 'groupLocations', groupLocations);
	},

	/**
	 * Set initial menu groups array
	 *
	 * @param {RestaurantState} state
	 * @param {MenuGroup[]} initialMenuGroups
	 * @return {void}
	 */
	SET_INITIAL_MENU_GROUPS(state: RestaurantState, initialMenuGroups: MenuGroup[]): void {
		Vue.set(state, 'initialMenuGroups', initialMenuGroups);
	},

	/**
	 * Set initial menus array and set the item list of all items
	 *
	 * @param {RestaurantState} state
	 * @param {Menu[]} initialMenus
	 * @return {void}
	 */
	SET_INITIAL_MENUS(state: RestaurantState, initialMenus: Menu[]): void {
		Vue.set(state, 'initialMenus', initialMenus);
		initialMenus.forEach((menu: Menu) => {
			if(menu.sections) {
				menu.sections.forEach((section: MenuSection) => {
					section.items.forEach((item: MenuItem) => {
						state.itemList.push(item);
					});
				});
			}
			else {
				menu.items.forEach((item: MenuItem) => {
					state.itemList.push(item);
				});
			}
		});
	},

	/**
	 * Set filtered menus array
	 *
	 * @param {RestaurantState} state
	 * @param {Menu[]} filteredMenus
	 * @return {void}
	 */
	SET_FILTERED_MENUS(state: RestaurantState, filteredMenus: Menu[]): void {
		Vue.set(state, 'filteredMenus', filteredMenus);
	},

	SET_ROTATIONAL_MENUS(state: RestaurantState, rotationalMenus: RotationalMenu[]): void {
		// Update the date range if the week of the current date is before the week of the first rotational menu date
		// This will happen if the ordering cutoff pushes the next valid ordering date to the next week
		// Ex: If it's Thursday and the cutoff is 5 days, the next valid date will be next the Tuesday of next week
		// This is weekly-view logic only; the monthly view keeps its full month range and selectedMonth param.
		if (rotationalMenus.length && state.calendarViewMode !== 'monthly') {
			const selectedWeek = state.rotationalMenuDateRange?.start ? DateTime.fromISO(state.rotationalMenuDateRange.start) : null;
			const rotationalMenuDate = DateTime.fromISO(rotationalMenus[0].date);

			if (selectedWeek && rotationalMenuDate.startOf('week') > selectedWeek.startOf('week')) {
				const updatedCurrentDate = rotationalMenuDate.startOf('week').toISODate();

				Vue.set(state, 'rotationalMenuDateRange', {
					start: updatedCurrentDate,
					end: rotationalMenuDate.endOf('week').toISODate()
				});

				router.replace({ query: { ...router.currentRoute.query, selectedWeek: updatedCurrentDate } });
			}

		}

		const formattedRotationalMenus = formatAndSortRotationalMenus(rotationalMenus, state.restaurant);

		// Apply subsidy pricing (if any) to the menus before setting them in state
		if (store.getters['restaurant/hasSubsidies']) {
			const authState: AuthState = store.state.auth;
			const cartState: CartState = store.state.cart;
			const eligibleSubsidies = getEligibleSubsidies(state.restaurant.subsidies, authState);

			if (eligibleSubsidies.length) {
				applySubsidiesToRotationalMenus(formattedRotationalMenus, eligibleSubsidies, cartState.checkout.memberInfo?.provider);
			}
		}

		Vue.set(state, 'rotationalMenus', formattedRotationalMenus);
	},

	SET_ROTATIONAL_MENU_DATE_RANGE(state: RestaurantState, dateRange: DateRange): void {
		Vue.set(state, 'rotationalMenuDateRange', dateRange);
	},

	SET_ROTATIONAL_MENUS_FETCHING(state: RestaurantState, fetching: boolean): void {
		Vue.set(state, 'rotationalMenusFetching', fetching);
	},

	SET_CALENDAR_VIEW_MODE(state: RestaurantState, mode: CalendarViewMode): void {
		Vue.set(state, 'calendarViewMode', mode);
	},

	SET_REPEATING_ORDERS_MENUS(state: RestaurantState, rotationalMenus: RotationalMenu[]): void {
		const formattedRotationalMenus = formatAndSortRotationalMenus(rotationalMenus, state.restaurant);

		// Apply subsidy pricing (if any) to the menus before setting them in state
		if (store.getters['restaurant/hasSubsidies']) {
			const authState: AuthState = store.state.auth;
			const cartState: CartState = store.state.cart;
			const eligibleSubsidies = getEligibleSubsidies(state.restaurant.subsidies, authState);

			if (eligibleSubsidies.length) {
				applySubsidiesToRotationalMenus(formattedRotationalMenus, eligibleSubsidies, cartState.checkout.memberInfo?.provider);
			}
		}

		Vue.set(state, 'repeatingOrdersMenus', formattedRotationalMenus);
	},

	SET_REPEATING_ORDERS_UNAVAILABLE_DATES(state: RestaurantState, dates: string[]): void {
		Vue.set(state, 'repeatingOrdersUnavailableDates', dates);
	},

	SET_REPEATING_ORDERS_SELECTED_MEAL_TYPE(state: RestaurantState, mealType: string): void {
		Vue.set(state, 'repeatingOrdersSelectedMealType', mealType);
	},

	SET_ANNOUNCEMENTS(state: RestaurantState, announcements: Announcement[]): void {
		Vue.set(state, 'announcements', announcements);
	},

	/**
	 * Apply member pricing discounts on items/options that contain matching promos
	 * (sets adjustedPrice field)
	 *
	 * @param {RestaurantState} state
	 * @param {Menu[]} menus
	 * @return {void}
	 */
	SET_MEMBER_PRICES(state: RestaurantState, menus: Menu[]): void {
		const cartState: CartState = store.state.cart;
		if(cartState.checkout.memberInfo && cartState.checkout.memberInfo.promos && cartState.checkout.memberInfo.promos.length) {

			// Apply member discounts to cart items
			if(cartState.items && cartState.items.length) {
				cartState.items.forEach((item: OrderItem) => {

					// We need to know if the cart item has pricing options because if it does we don't want to manipulate the price
					// directly from the item
					const hasPricingOptions = item.orderOptions && item.orderOptions.some((orderOption: OrderOptionGroup) => orderOption.optionGroupType === 'pricing') || false;
					compareMemberItem(item, cartState.checkout.memberInfo!, 'orderOptions', hasPricingOptions);
				});
			}

			// Apply member discounts to menu items
			menus.forEach((menu: Menu) => {

				// Section items
				if(menu.sections) {
					menu.sections.forEach((section: MenuSection) => {
						section.items.forEach((item: MenuItem) => {
							compareMemberItem(item, cartState.checkout.memberInfo!);
						});
					});
				}

				// Menu items
				else {
					menu.items.forEach((item: MenuItem) => {
						compareMemberItem(item, cartState.checkout.memberInfo!);
					});
				}
			});
		}
		Vue.set(state, 'memberMenus', menus);
	},

	/**
	 * Clear any previously applied member pricing discounts from items/options
	 *
	 * @param {RestaurantState} state
	 * @param {Menu[]} menus
	 * @return {void}
	 */
	CLEAR_MEMBER_PRICES(state: RestaurantState, menus: Menu[]): void {
		const cartState: CartState = store.state.cart;

		menus.forEach((menu: Menu) => {
			const clearItems = (items: MenuItem[]) => items.forEach((item: MenuItem) => clearItemAdjustedPrices(item));

			if(menu.sections) {
				menu.sections.forEach((section: MenuSection) => clearItems(section.items));
			}
			else {
				clearItems(menu.items);
			}
		});

		if(cartState.items?.length) {
			cartState.items.forEach((item: OrderItem) => {
				clearItemAdjustedPrices(item, 'orderOptions');
			});
		}
	},

	/**
	 * Apply subsidy pricing discounts on items/options based on restaurant subsidies
	 * and user eligibility (sets adjustedPrice field).
	 *
	 * @param {RestaurantState} state
	 * @param {Menu[]} menus
	 * @return {void}
	 */
	SET_SUBSIDY_PRICES(state: RestaurantState, menus: Menu[]): void {
		const authState: AuthState = store.state.auth;
		const cartState: CartState = store.state.cart;

		// Don't apply subsidies if member pricing is available (mutual exclusivity - for now)
		if (cartState.checkout.memberInfo?.provider === 'ELB' || cartState.checkout.memberInfo?.provider === 'SPH') {
			return;
		}

		if(store.getters['restaurant/hasSubsidies']) {
			// Get eligible subsidies for the current user
			const eligibleSubsidies = getEligibleSubsidies(state.restaurant.subsidies, authState);

			if(eligibleSubsidies.length) {
				// Apply subsidy discounts to cart items (sets adjustedPrice)
				if(cartState.items && cartState.items.length) {
					cartState.items.forEach((item: OrderItem) => {
						const hasPricingOptions = item.orderOptions && item.orderOptions.some((orderOption: OrderOptionGroup) => orderOption.optionGroupType === 'pricing') || false;
						compareSubsidyItem(item, eligibleSubsidies, 'orderOptions', hasPricingOptions);
					});
				}

				// Apply subsidy discounts to regular menu items (sets adjustedPrice) - for non-rotational menus
				const processItems = (items: MenuItem[]) => {
					items.forEach((item) => {
						compareSubsidyItem(item, eligibleSubsidies, 'options', false);
					});
				};
				menus.forEach((menu: Menu) => {
					if (menu.sections) {
						menu.sections.forEach((section: MenuSection) => processItems(section.items));
					}
					else if (menu.items) {
						processItems(menu.items);
					}
				});
			}
		}
		// TODO - Need additional testing to ensure that subsidy works as expected for normal menus (non rotational)
		Vue.set(state, 'memberMenus', menus);
	},

	SET_INQUIRY_FORM(state: RestaurantState, form: InquiryForm): void {
		Vue.set(state, 'inquiryForm', form);
	},

	/**
	 * Set payment config payload
	 *
	 * @param {RestaurantState} state
	 * @param {PaymentConfig} payload
	 * @return {void}
	 */
	SET_PAYMENT_CONFIG(state: RestaurantState, payload: PaymentConfig): void {
		Vue.set(state, 'paymentConfig', payload);
	},

	/**
	 * Set the css loaded state
	 *
	 * @param {RestaurantState} state
	 * @param {boolean} loaded
	 * @return {void}
	 */
	SET_CSS_LOADED(state: RestaurantState, loaded: boolean): void {
		Vue.set(state, 'cssLoaded', loaded);
	},

	RESET_RESTAURANT_STATE(state: RestaurantState): void {
		state.restaurant = {} as Restaurant;
		state.initialMenuGroups = [];
		state.initialMenus = [];
		state.itemList = [];
		state.memberMenus = [];
		state.filteredMenus = [];
		state.paymentConfig = null;
		applyCurrencyToI18n(store.getters['restaurant/getCurrency']);
	}
};
